import { Customer, CustomerAddress } from "../types";

/**
 * Get customer's full name, handling optional last name
 * @param customer Customer object or partial customer with first_name and last_name
 * @returns Full name string (first name + last name if exists)
 */
export const getCustomerFullName = (
    customer: Pick<Customer, "first_name" | "last_name"> | null | undefined
): string => {
    if (!customer) return "";
    
    const firstName = customer.first_name?.trim() || "";
    const lastName = customer.last_name?.trim() || "";
    
    if (firstName && lastName) {
        return `${firstName} ${lastName}`;
    }
    
    return firstName || "";
};

/**
 * Pick "the" default address out of a customer's address list — prefers a
 * SHIPPING address explicitly marked default, then any SHIPPING address,
 * then any address marked default, then just the first one. Same fallback
 * chain previously copy-pasted in telegramService.ts and posSlice.ts;
 * extracted here as the one place to update if that priority ever changes.
 */
export const getDefaultAddress = (
    addresses: CustomerAddress[] | null | undefined
): CustomerAddress | undefined => {
    if (!addresses || addresses.length === 0) return undefined;

    return (
        addresses.find((a) => a.is_default && a.address_type === "SHIPPING") ||
        addresses.find((a) => a.address_type === "SHIPPING") ||
        addresses.find((a) => a.is_default) ||
        addresses[0]
    );
};

/**
 * Format customer display name with optional email/phone
 * @param customer Customer object
 * @returns Formatted string like "John Doe - john@example.com (123-456-7890)"
 */
export const getCustomerDisplayName = (customer: Customer | null | undefined): string => {
    if (!customer) return "";
    
    const fullName = getCustomerFullName(customer);
    const parts = [fullName];
    
    if (customer.email) {
        parts.push(customer.email);
    }
    
    if (customer.phone) {
        parts.push(`(${customer.phone})`);
    }
    
    return parts.filter(Boolean).join(" - ");
};
